import { createFileRoute } from "@tanstack/react-router";
import React, { useMemo, useState } from "react";
import { AlertCircle, Check, Download, FileText, Loader2, Plus, Sparkles } from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import { Field } from "@/components/forms";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  downloadBlob,
  emptyResume,
  useDownloadCV,
  useMyCV,
  type CvExperience,
  type StudentResume,
} from "@/hooks/useCv";

export const Route = createFileRoute("/student/cv/")({ component: StudentCv });

/** The labels shown when the platform has nothing to fill in for a field. */
const completenessLabels: Record<string, string> = {
  name: "Add your name to your profile",
  email: "Add an email address to your profile",
  objective: "Write a short objective below",
  education: "Add your university and field of study to your profile",
  courses: "Pass 70% of a course's lessons to earn your first certificate",
};

function CvPreview({ resume }: { resume: StudentResume }) {
  const education = resume.education ?? [];
  const experience = resume.experience ?? [];
  const projects = resume.projects ?? [];
  const skills = resume.skills ?? [];
  const contact = resume.contact ?? {};

  const contactLine = [contact.email, contact.phone, contact.address, contact.website]
    .filter(Boolean)
    .join("  |  ");

  return (
    // A white sheet in both themes, so the preview reads as the printed
    // document it will become rather than as page chrome.
    <div className="min-h-[520px] rounded-md border border-border bg-white p-6 text-[13.5px] leading-[1.55] text-neutral-900 md:p-10">
      <p className="text-center text-[22px] font-medium tracking-[0.06em] uppercase md:text-[27px]">
        {resume.name || "Your Name"}
      </p>

      {contactLine && (
        <p className="mt-1.5 text-center text-[12.5px] text-neutral-600">{contactLine}</p>
      )}

      {resume.objective && <p className="mt-4 text-[13px] text-neutral-700">{resume.objective}</p>}

      <Section title="Education">
        {education.length === 0 ? (
          <Empty text="Add your university and field of study to your profile." />
        ) : (
          education.map((edu, index) => (
            <div key={`${edu.institution}-${index}`} className="mb-2.5">
              <p className="text-[14px] font-medium">{edu.institution || "—"}</p>
              <p className="text-[13px] text-neutral-700">
                {[edu.degree, edu.year].filter(Boolean).join(" — ")}
              </p>
            </div>
          ))
        )}
      </Section>

      <Section title="Experience">
        {experience.length === 0 ? (
          <Empty text="Apply for a work slot to build verified experience here." />
        ) : (
          experience.map((exp, index) => <ExperienceBlock key={index} exp={exp} />)
        )}
      </Section>

      <Section title="Projects and Certifications">
        {projects.length === 0 ? (
          <Empty text="Pass 70% of a course's lessons to earn your first certificate." />
        ) : (
          projects.map((project, index) => (
            <div key={`${project.cert_number}-${index}`} className="mb-2.5">
              <p className="text-[14px] font-medium">{project.title}</p>
              <p className="text-[12.5px] text-neutral-600">
                {[project.source, project.issued_on, project.cert_number].filter(Boolean).join(" — ")}
              </p>
            </div>
          ))
        )}
      </Section>

      <Section title="Technical Skills">
        {skills.length === 0 ? (
          <Empty text="Add skills to your profile to have them listed here." />
        ) : (
          <p className="text-[13px]">{skills.join(", ")}</p>
        )}
      </Section>

      <p className="mt-6 text-[10.5px] text-neutral-500 italic">
        Generated by Taskpart on {resume.generated_at}. Verified hours and certificates are drawn
        from Taskpart records.
      </p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-5">
      <h3 className="border-b border-neutral-200 pb-1.5 text-[12.5px] font-medium tracking-[0.08em] text-neutral-800 uppercase">
        {title}
      </h3>
      <div className="mt-1.5">{children}</div>
    </section>
  );
}

function Empty({ text }: { text: string }) {
  return <p className="text-[12.5px] text-neutral-400 italic">{text}</p>;
}

function ExperienceBlock({ exp }: { exp: CvExperience }) {
  const dates = [exp.start_date, exp.end_date].filter(Boolean).join(" - ");
  const selfReported = exp.source === "self reported";

  return (
    <div className="mb-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4">
        <p className="text-[14px] font-medium">
          {[exp.role, exp.company].filter(Boolean).join(" — ")}
        </p>
        <p className="text-[12px] whitespace-nowrap text-neutral-600">
          {[exp.location, dates].filter(Boolean).join(" — ")}
        </p>
      </div>

      {!!exp.hours && (
        <p className="text-[12px] text-neutral-600 italic">
          {exp.verified ? (
            <>
              {`${exp.hours} verified hours`}
              {exp.completed ? " — placement completed" : " — partner approved"}
            </>
          ) : (
            `${exp.hours} self-reported hours — not verified by Taskpart`
          )}
        </p>
      )}

      {(exp.highlights ?? []).length > 0 && (
        <ul className="mt-1 list-disc space-y-0.5 pl-5 text-[13px] leading-normal">
          {(exp.highlights ?? []).map((highlight, index) => (
            <li key={index}>{highlight}</li>
          ))}
        </ul>
      )}

      {(exp.skills ?? []).length > 0 && (
        <p className="mt-1 text-[12.5px]">
          <span className="font-medium">Skills: </span>
          {(exp.skills ?? []).join(", ")}
        </p>
      )}

      {selfReported && (
        <p className="mt-1 text-[11px] text-amber-700">Self reported — not verified by Taskpart</p>
      )}
    </div>
  );
}

function StudentCv() {
  const { data: resume, isLoading } = useMyCV();
  const download = useDownloadCV();

  const [objective, setObjective] = useState<string | null>(null);
  const [highlightSkills, setHighlightSkills] = useState("");
  const [extra, setExtra] = useState<CvExperience[]>([]);

  // The overrides are applied on top of the fetched resume so the preview shows
  // what will be downloaded. Verified fields are never editable here.
  const preview = useMemo<StudentResume>(() => {
    const base = resume ?? emptyResume;
    // These flags mirror what the server forces on extra_experience, so the
    // preview cannot present a typed entry as platform verified.
    const extraClean = extra
      .filter((item) => item.role?.trim() || item.company?.trim())
      .map((item) => ({ ...item, source: "self reported", verified: false, completed: false }));
    const extraSkills = highlightSkills
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const mergedSkills = [...(base.skills ?? [])];
    for (const skill of extraSkills) {
      if (!mergedSkills.some((s) => s.toLowerCase() === skill.toLowerCase())) {
        mergedSkills.push(skill);
      }
    }

    return {
      ...base,
      objective: objective ?? base.objective ?? "",
      skills: mergedSkills,
      experience: [...(base.experience ?? []), ...extraClean],
    };
  }, [resume, objective, highlightSkills, extra]);

  const missing = (preview.data_completeness ?? [])
    .map((key) => completenessLabels[key])
    .filter(Boolean) as string[];

  const handleDownload = () => {
    download.mutate(
      {
        objective: preview.objective,
        highlight_skills: highlightSkills
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        extra_experience: extra.filter((item) => item.role?.trim() || item.company?.trim()),
      },
      {
        onSuccess: (blob) => {
          const slug = (preview.name || "cv").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
          downloadBlob(blob, `taskpart-cv-${slug}.pdf`);
        },
      },
    );
  };

  const updateExtra = (index: number, patch: Partial<CvExperience>) => {
    const next = [...extra];
    next[index] = { ...extra[index], ...patch };
    setExtra(next);
  };

  return (
    <div>
      <PageHeader
        title="CV builder"
        subtitle="Your CV is built from your profile, work placements and certificates."
        breadcrumbs={[
          { label: "Dashboard", path: "/student/dashboard" },
          { label: "CV Builder" },
        ]}
        action={
          <Button onClick={handleDownload} disabled={download.isPending || isLoading}>
            {download.isPending ? <Loader2 className="animate-spin" /> : <Download />}
            {download.isPending ? "Preparing..." : "Download PDF"}
          </Button>
        }
      />

      <div className="space-y-6">
        {(download.isError || missing.length > 0) && (
          <div className="space-y-3">
            {download.isError && (
              <Alert variant="destructive">
                <AlertCircle />
                <AlertDescription>Could not generate the PDF. Please try again.</AlertDescription>
              </Alert>
            )}

            {missing.length > 0 && (
              <Alert variant="info">
                <Sparkles />
                <div>
                  <AlertTitle>Fill these in for a complete CV</AlertTitle>
                  <AlertDescription>
                    <ul className="mt-1 list-disc space-y-0.5 pl-4">
                      {missing.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </div>
              </Alert>
            )}
          </div>
        )}

        <div className="grid gap-6 md:grid-cols-12">
          <div className="min-w-0 md:col-span-7">
            {isLoading ? <SkeletonBox /> : <CvPreview resume={preview} />}
          </div>

          <div className="min-w-0 md:col-span-5">
            <div className="divide-y divide-border rounded-md border border-border bg-card">
              <div className="space-y-2 p-4 md:p-5">
                <h2 className="flex items-center gap-2 text-sm font-medium text-foreground">
                  <FileText className="size-4 text-muted-foreground" aria-hidden />
                  Objective
                </h2>
                <Field hint="Leave blank to use the bio from your profile.">
                  <Textarea
                    aria-label="Objective"
                    rows={3}
                    value={objective ?? preview.objective ?? ""}
                    onChange={(e) => setObjective(e.target.value)}
                    placeholder="Final-year computer science student seeking practical experience in data analysis."
                  />
                </Field>
              </div>

              <div className="space-y-2 p-4 md:p-5">
                <h2 className="text-sm font-medium text-foreground">Highlight skills</h2>
                <Field hint="Comma separated. These are added to the skills from your profile and placements.">
                  <Input
                    aria-label="Highlight skills"
                    value={highlightSkills}
                    onChange={(e) => setHighlightSkills(e.target.value)}
                    placeholder="Stakeholder communication, Tableau"
                  />
                </Field>
              </div>

              <div className="space-y-3 p-4 md:p-5">
                <div>
                  <h2 className="text-sm font-medium text-foreground">Other experience</h2>
                  <p className="text-caption text-muted-foreground">
                    Anything outside Taskpart. Listed as self reported, never as verified.
                  </p>
                </div>

                {extra.map((item, index) => (
                  <fieldset key={index} className="space-y-3 rounded-sm border border-border p-3">
                    <legend className="sr-only">Experience {index + 1}</legend>
                    <Field label="Role">
                      <Input
                        value={item.role ?? ""}
                        onChange={(e) => updateExtra(index, { role: e.target.value })}
                      />
                    </Field>
                    <Field label="Organisation">
                      <Input
                        value={item.company ?? ""}
                        onChange={(e) => updateExtra(index, { company: e.target.value })}
                      />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Start">
                        <Input
                          value={item.start_date ?? ""}
                          onChange={(e) => updateExtra(index, { start_date: e.target.value })}
                        />
                      </Field>
                      <Field label="End">
                        <Input
                          value={item.end_date ?? ""}
                          onChange={(e) => updateExtra(index, { end_date: e.target.value })}
                        />
                      </Field>
                    </div>
                    <Field label="What you did">
                      <Textarea
                        rows={2}
                        value={item.description ?? ""}
                        onChange={(e) => updateExtra(index, { description: e.target.value })}
                      />
                    </Field>
                    <div className="flex justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setExtra(extra.filter((_, i) => i !== index))}
                      >
                        Remove
                      </Button>
                    </div>
                  </fieldset>
                ))}

                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => setExtra([...extra, { role: "", company: "" }])}
                >
                  <Plus />
                  Add experience
                </Button>
              </div>

              <div className="space-y-2 p-4 md:p-5">
                <h2 className="text-sm font-medium text-foreground">What is verified</h2>
                <ul className="space-y-2">
                  <VerifiedRow ok={!!resume?.experience?.some((e) => e.verified)}>
                    Placements with partner-approved hours
                  </VerifiedRow>
                  <VerifiedRow ok={!!resume?.projects?.length}>
                    Course and placement certificates
                  </VerifiedRow>
                  <VerifiedRow ok={!!resume?.skills?.length}>
                    Skills from your profile and work logs
                  </VerifiedRow>
                </ul>
                <p className="text-caption text-muted-foreground">
                  Everything else on the CV is what you typed. Verified hours cannot be edited.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function VerifiedRow({ ok, children }: { ok: boolean; children: React.ReactNode }) {
  return (
    <li className="flex items-center gap-2 text-[13px] text-body">
      {ok ? (
        <Check className="size-4 shrink-0 text-success" aria-label="Verified" />
      ) : (
        <AlertCircle className="size-4 shrink-0 text-muted-foreground" aria-label="Not yet" />
      )}
      <span>{children}</span>
      {!ok && <Badge variant="muted">nothing yet</Badge>}
    </li>
  );
}

function SkeletonBox() {
  return (
    <div className="rounded-md border border-border bg-card p-5" aria-busy="true">
      <Skeleton className="mb-6 h-6 w-1/2" />
      <div className="space-y-2.5">
        {[100, 60, 90, 70, 85].map((w, i) => (
          <Skeleton key={i} className="h-3" style={{ width: `${w}%` }} />
        ))}
      </div>
    </div>
  );
}
